import ListItem from "@/components/ListItem/ListItem";
import { ObjectHeadingWrapper } from "@/features/person/components/PersonObjectHeading";
import { useNavigate } from "@/router";
import { BackofficePerson } from "@/types";
import { MaterialNames } from "@/components/material_symbol_names";
import SubSectionHeading from "@/components/SubSectionHeading";
import {
  phoneOptions,
  emailOptions,
  addressOptions,
  employmentOptions,
  personalOptions,
  facilityOptions,
  jobApplicationOptions,
  programOptions,
  groupOptions
} from "./utils";

// Import types that were moved from PersonDetailsPanel
export interface CustomActionButton {
  text: string;
  variant?: "text" | "outlined" | "filled";
  onClick: () => void;
  testId?: string;
  iconName?: MaterialNames;
}

interface PersonSideSheetContentProps {
  person: BackofficePerson;
  pgaId: string;
  customActionButtons?: CustomActionButton[];
  showDefaultSections?: boolean;
  showPersonalDetails?: boolean;
  showEmployment?: boolean;
  showFacility?: boolean;
  showJobApplications?: boolean;
  alternateEmail?: string;
  alternatePhone?: string;
  universityName?: string;
  navigate: ReturnType<typeof useNavigate>;
  children?: React.ReactNode;
}

export default function PersonSideSheetContent({
  person,
  pgaId,
  customActionButtons,
  showDefaultSections = true,
  showPersonalDetails = false,
  showEmployment = false,
  showFacility = false,
  showJobApplications = false,
  alternateEmail,
  alternatePhone,
  universityName,
  navigate,
  children
}: PersonSideSheetContentProps) {
  // Use university name from person data if not explicitly provided
  const effectiveUniversityName = universityName || person?.university?.name;

  const sections = [
    // Always shown sections (default sections)
    // Show Program section if program type is available, otherwise show Groups section
    ...(person && programOptions(person, effectiveUniversityName).length > 0
      ? [
          {
            heading: "Program",
            options: programOptions(person, effectiveUniversityName)
          }
        ]
      : person && groupOptions(person).length > 0
        ? [
            {
              heading: "Groups",
              options: groupOptions(person)
            }
          ]
        : []),
    {
      heading: "Phone",
      options: phoneOptions(person?.phoneNumbers, alternatePhone)
    },
    {
      heading: "Email",
      options: emailOptions(person?.email, alternateEmail)
    },
    {
      heading: "Addresses",
      options: addressOptions(person?.addresses)
    },
    // Optional sections - explicitly controlled by props
    ...(person && showPersonalDetails && personalOptions(person).length > 0
      ? [
          {
            heading: "Personal",
            options: personalOptions(person)
          }
        ]
      : []),
    ...(person && showJobApplications && jobApplicationOptions(person, navigate).length > 0
      ? [
          {
            heading: "Job Application History",
            options: jobApplicationOptions(person, navigate),
            headingAction: pgaId ? () => window.open(`/people/${pgaId}/career/job-applications`, "_blank") : undefined
          }
        ]
      : []),
    ...(person && showEmployment && employmentOptions(person.employments, navigate, pgaId, 3).length > 0
      ? [
          {
            heading: "Employment",
            options: employmentOptions(person.employments, navigate, pgaId, 3),
            headingAction: pgaId ? () => window.open(`/people/${pgaId}/career/employment`, "_blank") : undefined
          }
        ]
      : []),
    ...(person &&
    showFacility &&
    facilityOptions(
      person.employments?.find((e) => e.isPrimary),
      navigate
    ).length > 0
      ? [
          {
            heading: "Facility",
            options: facilityOptions(
              person.employments?.find((e) => e.isPrimary),
              navigate
            )
          }
        ]
      : [])
  ];

  const allSections = showDefaultSections ? sections : [];

  return (
    <div>
      <ObjectHeadingWrapper
        personData={{ person }}
        pgaId={pgaId}
        actionButtons={customActionButtons?.map((btn) => ({
          text: btn.text,
          variant: btn.variant,
          onClick: btn.onClick,
          testId: btn.testId,
          iconName: btn.iconName
        }))}
        variant="condensed"
        showMinimalMetadata={true}
        avatarSize="large"
      />

      {/* Default Sections */}
      {allSections.map((section: any, index: number) => (
        <div key={index} className="flex flex-col gap-2 items-start mt-8">
          <SubSectionHeading onClick={section.headingAction}>{section.heading}</SubSectionHeading>
          <div className="space-y-4 w-full">
            {section.options.map((option: any, optionIndex: number) => (
              <ListItem key={optionIndex} {...option} iconBackground={true} />
            ))}
          </div>
        </div>
      ))}

      {children}
    </div>
  );
}
